import { useTranslation } from 'react-i18next';

import type { ProjectListing } from '@/api/v4/projects.api';
import { getListingStageByDefaultName } from '@/features/listing-stage/helpers/get-listing-stage-by-default-name';
import { isDealStage, ListingStageName } from '@/features/listing-stage/types';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { modalState } from '@/state/modal.state';
import type { DropdownOption } from '@/ui/dropdown/dropdown';
import { WithDropdown } from '@/ui/dropdown/dropdown';
import { MenuButton } from '@/ui/menu-button/menu-button';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { useListingStages } from '../../../../listing-stage/use-listing-stages.query';
import { useIsPendingProjectListingStageMutation } from '../../use-pending-project-listing-stage-mutation';
import { useUpdateProjectListingStage } from '../../use-update-project-listing-stage.mutation';
import { useSetAtom } from 'jotai';

type ListingContextMenuItem =
  | 'goToCompanyPage'
  | 'moveToStage'
  | 'moveToNotRelevant'
  | 'sosaFeature'
  | 'removeFromProject';

const LISTING_STATUSES_CONTEXT_MENU: ListingStageName[] = [
  ListingStageName.discovery,
  ListingStageName.potentialFit,
  ListingStageName.contacted,
  ListingStageName.dueDiligence,
  ListingStageName.pilot,
  ListingStageName.deal,
];

export const ListingMenu = ({
  listing,
  blacklist,
}: {
  listing: ProjectListing;
  blacklist?: ListingContextMenuItem[];
}) => {
  const { t } = useTranslation('projects', {
    keyPrefix: 'listings',
  });
  const setModal = useSetAtom(modalState);
  const navigateTo = useNavigateTo();
  const { hasRequiredPermission } = useUserPermissions();
  const isPendingStageMutation = useIsPendingProjectListingStageMutation(
    listing.id,
  );
  const { listingStages } = useListingStages();
  const { editProjectListingStage } = useUpdateProjectListingStage();

  const isNotRelevant = listing.notRelevant;

  const moveToStatus = async (newStageName: ListingStageName) => {
    if (isPendingStageMutation) return;

    const stage = getListingStageByDefaultName(newStageName, listingStages);
    if (!stage) return;
    await editProjectListingStage({
      newProjectListingStageId: stage.id,
      projectListingId: listing.id,
    });
  };

  const menu: DropdownOption[] = [
    {
      label: t`goToCompanyPage`,
      isVisible:
        hasRequiredPermission(PERMISSION.ADD_ORGANIZATION) &&
        blacklist &&
        !blacklist.includes('goToCompanyPage'),
      onClick: () => navigateTo.company({ companyId: listing.organization.id }),
      icon: 'Check',
    },
    {
      label: t`moveStage`,
      isVisible:
        hasRequiredPermission(PERMISSION.UPDATE_COMPANY_LISTING_STATUS) &&
        blacklist &&
        !blacklist.includes('moveToStage'),
      icon: 'ArrowLeft',
      options: LISTING_STATUSES_CONTEXT_MENU.filter(
        listingStage =>
          !isDealStage(listingStage) &&
          (listingStages.find(stage => stage.name === listingStage) ||
            listingStage === ListingStageName.deal),
      ).map(listingStage => {
        const stage = getListingStageByDefaultName(listingStage, listingStages);
        return {
          label: stage?.displayName ?? stage?.name ?? listingStage,
          onClick: async () => {
            if (isPendingStageMutation) return;

            if (listingStage === ListingStageName.deal) {
              setModal({ state: 'deal', listingId: listing.id });
              return;
            }

            await moveToStatus(listingStage as ListingStageName);
          },
        };
      }),
    },
    {
      label: t`moveToNotRelevant`,
      isVisible:
        !isNotRelevant &&
        hasRequiredPermission(PERMISSION.UPDATE_COMPANY_LISTING_STATUS) &&
        blacklist &&
        !blacklist.includes('moveToNotRelevant'),
      onClick: async () => {
        if (isPendingStageMutation) return;

        await moveToStatus(ListingStageName.notRelevant);
      },
      icon: 'CloseCircle',
    },
    {
      label: t`removeFromProject`,
      isVisible:
        hasRequiredPermission(PERMISSION.REMOVE_COMPANIES_FROM_PROJECTS) &&
        blacklist &&
        !blacklist.includes('removeFromProject'),
      onClick: () => {
        setModal({
          state: 'deleteProjectListing',
          listing,
        });
      },
      icon: 'Bin',
    },
  ];

  return (
    <WithDropdown
      disabled={isPendingStageMutation}
      items={menu}
      options={{ placement: 'bottom-end' }}
      testId={`listing-menu-${listing.organization.id}`}
    >
      <MenuButton />
    </WithDropdown>
  );
};
